import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { get } from '../../utils/api';

export function useDocumentCategories({
  userOrganizationId,
  isPageEnabled,
  documentManagementLetters,
  documentManagementApplicationTermsAndConditions,
}) {
  const { data: documentCategoriesResponse } = useQuery(
    ['documentCategories', userOrganizationId],
    () => {
      return get(`/${userOrganizationId}/document-categories`);
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: isPageEnabled,
    },
  );

  const documentCategories = useMemo(() => {
    return documentCategoriesResponse?.filter((category) => {
      switch (category.name) {
        case 'Letter':
          return documentManagementLetters;
        case 'Application':
          return documentManagementApplicationTermsAndConditions;
        default:
          return true;
      }
    });
  }, [
    documentCategoriesResponse,
    documentManagementLetters,
    documentManagementApplicationTermsAndConditions,
  ]);

  return documentCategories;
}

/**
 * Affordable programs a letter can be tracked as a recertification notice for, as select options.
 * Empty while the recert-notices flag is off or when the organization runs no affordable programs,
 * which keeps the recertification notice fields hidden.
 */
export function useRecertNoticePrograms({ userOrganizationId, isEnabled }) {
  const { data: recertNoticePrograms } = useQuery(
    ['recertNoticePrograms', userOrganizationId],
    () =>
      get(
        `/${userOrganizationId}/centralized-documents/recert-notice-programs`,
      ),
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: isEnabled,
    },
  );

  return useMemo(
    () =>
      isEnabled
        ? (recertNoticePrograms ?? []).map((program) => ({
            value: program.id,
            text: program.name,
          }))
        : [],
    [isEnabled, recertNoticePrograms],
  );
}
